import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import {
  setAllStaff,
  addStaff,
  updateStaffInList,
  removeStaff,
} from "../../store/adminSlice";
import { adminApi } from "../../services/api";

const MAIN_PERM_LABELS = {
  dashboard: "Dashboard Stats",
  users: "Users Tab",
  kyc: "KYC Tab",
  trades: "Trades Tab",
  deposits: "Deposits Tab",
  withdrawals: "Withdrawals Tab",
  settings: "Settings Tab",
  staff: "Staff Management",
  leads: "Leads (Staging)",
  groxion_leads: "Anzo Leads",
  retention_leads: "Retention Leads",
};

const USER_DETAIL_PERM_LABELS = {
  canViewOverview: "View Overview",
  canViewTrades: "View Trades",
  canViewDeposits: "View Deposits",
  canViewWithdrawals: "View Withdrawals",
  canViewKyc: "View KYC",
  canViewActions: "View Actions",
  canEditUser: "Edit User Fields",
  canModifyTrade: "Modify Trade P&L",
  canPlaceTrade: "Place Trade",
  canAdjustBalance: "Adjust Balance",
  canChangePassword: "Change Password",
  canApproveWithdrawal: "Approve/Reject Withdrawal",
  canApproveKyc: "Approve/Reject KYC",
  canSuspendUser: "Suspend/Activate User",
};

const STAFF_TYPES = ["conversion", "retention"];

const StaffManagement = () => {
  const dispatch = useDispatch();
  const { allStaff, adminUser } = useSelector((state) => state.admin);
  const [showForm, setShowForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    frejunNumber: "",
    frejunEmail: "",
    smtpEmails: [{ name: "", email: "", password: "" }],
    role: "staff",
    staffType: "conversion",
    teamLeadId: "",
    permissions: {
      dashboard: false,
      users: false,
      kyc: false,
      trades: false,
      deposits: false,
      withdrawals: false,
      settings: false,
      staff: false,
      leads: false,
      groxion_leads: false,
      retention_leads: false,
      canViewOverview: false,
      canViewTrades: false,
      canViewDeposits: false,
      canViewWithdrawals: false,
      canViewKyc: false,
      canViewActions: false,
      canEditUser: false,
      canModifyTrade: false,
      canPlaceTrade: false,
      canAdjustBalance: false,
      canChangePassword: false,
      canApproveWithdrawal: false,
      canApproveKyc: false,
      canSuspendUser: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const res = await adminApi.get("/admin/staff");
      dispatch(setAllStaff(res.data?.staff || []));
    } catch (e) {}
  };

  const defaultPerms = () => ({
    dashboard: false,
    users: false,
    kyc: false,
    trades: false,
    deposits: false,
    withdrawals: false,
    settings: false,
    staff: false,
    leads: false,
    groxion_leads: false,
    retention_leads: false,
    canViewOverview: false,
    canViewTrades: false,
    canViewDeposits: false,
    canViewWithdrawals: false,
    canViewKyc: false,
    canViewActions: false,
    canEditUser: false,
    canModifyTrade: false,
    canPlaceTrade: false,
    canAdjustBalance: false,
    canChangePassword: false,
    canApproveWithdrawal: false,
    canApproveKyc: false,
    canSuspendUser: false,
  });

  const resetForm = () => {
    setForm({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phone: "",
      frejunNumber: "",
      frejunEmail: "",
      smtpEmails: [{ name: "", email: "", password: "" }],
      role: "staff",
      staffType: "conversion",
      teamLeadId: "",
      permissions: defaultPerms(),
    });
    setEditStaff(null);
    setShowForm(false);
  };

  const handleEdit = (staff) => {
    setEditStaff(staff);
    const existingPerms = staff.permissions || {};
    let emailAccounts = [{ name: "", email: "", password: "" }];

    if (staff.smtpEmails) {
      if (typeof staff.smtpEmails === "string") {
        try {
          emailAccounts = JSON.parse(staff.smtpEmails);
        } catch (e) {}
      } else if (Array.isArray(staff.smtpEmails)) {
        emailAccounts = staff.smtpEmails;
      }
    }

    setForm({
      email: staff.email || "",
      password: "",
      firstName: staff.firstName || "",
      lastName: staff.lastName || "",
      phone: staff.phone || "",
      frejunNumber: staff.frejunNumber || "",
      frejunEmail: staff.frejunEmail || "",
      smtpEmails:
        emailAccounts.length > 0
          ? emailAccounts
          : [{ name: "", email: "", password: "" }],
      role: staff.role || "staff",
      staffType: staff.staffType || "conversion",
      teamLeadId: staff.teamLeadId || "",
      permissions: { ...defaultPerms(), ...existingPerms },
    });
    setShowForm(true);
  };

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  const handleEmailChange = (index, field, value) => {
    const updated = [...form.smtpEmails];
    updated[index][field] = value;
    setForm({ ...form, smtpEmails: updated });
  };

  const addEmailAccount = () => {
    setForm({
      ...form,
      smtpEmails: [...form.smtpEmails, { name: "", email: "", password: "" }],
    });
  };

  const removeEmailAccount = (index) => {
    const updated = form.smtpEmails.filter((_, i) => i !== index);
    setForm({
      ...form,
      smtpEmails:
        updated.length > 0 ? updated : [{ name: "", email: "", password: "" }],
    });
  };

  const handleSubmit = async () => {
    if (!form.email || (!editStaff && !form.password)) {
      dispatch(
        showToast({ message: "Email and password required", type: "error" }),
      );
      return;
    }

    // Filter out empty email entries
    const validEmails = form.smtpEmails.filter(
      (e) => e.email.trim() && e.password.trim(),
    );

    setIsSubmitting(true);
    try {
      const payload = {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        frejunNumber: form.frejunNumber,
        frejunEmail: form.frejunEmail,
        smtpEmails: validEmails,
        role: form.role,
        staffType: form.staffType,
        teamLeadId: form.role === "staff" ? form.teamLeadId || null : null,
        permissions: form.permissions,
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (editStaff) {
        const res = await adminApi.put(`/admin/staff/${editStaff.id}`, payload);
        dispatch(updateStaffInList({ id: editStaff.id, data: res.data.staff }));
        dispatch(showToast({ message: "Staff updated", type: "success" }));
      } else {
        const res = await adminApi.post("/admin/staff", payload);
        dispatch(addStaff(res.data.staff));
        dispatch(showToast({ message: "Staff created", type: "success" }));
      }
      resetForm();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed",
          type: "error",
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (staff) => {
    try {
      const res = await adminApi.put(`/admin/staff/${staff.id}/toggle`, {
        isActive: !staff.isActive,
      });
      dispatch(updateStaffInList({ id: staff.id, data: res.data.staff }));
      dispatch(
        showToast({
          message: `Staff ${staff.isActive ? "deactivated" : "activated"}`,
          type: "success",
        }),
      );
    } catch (e) {}
  };

  const handleDelete = async (staff) => {
    if (!confirm(`Delete ${staff.email}?`)) return;
    try {
      await adminApi.delete(`/admin/staff/${staff.id}`);
      dispatch(removeStaff(staff.id));
      dispatch(showToast({ message: "Staff deleted", type: "success" }));
    } catch (e) {}
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "super_admin":
        return "bg-[#7c3aed] text-white border-[#6d28d9]";
      case "admin":
        return "bg-[#d97706] text-white border-[#b45309]";
      case "manager":
        return "bg-[#0d9488] text-white border-[#0f766e]";
      case "team_lead":
        return "bg-[#1e3a5f] text-white border-[#0b1e33]";
      case "staff":
        return "bg-[#2563eb] text-white border-[#1d4ed8]";
      default:
        return "bg-[#64748b] text-white border-[#475569]";
    }
  };

  const getStaffTypeBadge = (staffType) => {
    if (staffType === "retention") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#fef3c7] text-[#92400e] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#fcd34d] border-b-[2px]">
          <i className="fas fa-lock text-[9px]" /> RETENTION
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#ecfdf5] text-[#059669] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#a7f3d0] border-b-[2px]">
        <i className="fas fa-rotate text-[9px]" /> CONVERSION
      </span>
    );
  };

  const eligibleTeamLeads = allStaff.filter(
    (s) => s.role === "team_lead" && s.staffType === form.staffType,
  );

  const getTeamLeadName = (id) => {
    if (!id) return null;
    const tl = allStaff.find((s) => s.id === id);
    return tl ? `${tl.firstName} ${tl.lastName}` : null;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            STAFF MANAGEMENT
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            {allStaff.length} STAFF MEMBERS
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
        >
          <i className="fas fa-plus mr-1.5" /> ADD STAFF
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0]">
              {[
                "NAME",
                "EMAIL",
                "ROLE",
                "TYPE",
                "TEAM LEAD",
                "PHONE",
                "FREJUN NO.",
                "FREJUN EMAIL",
                "ASSIGNED EMAILS",
                "STATUS",
                "ACTIONS",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left px-5 py-3.5 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allStaff.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-user-tie text-[#94a3b8] text-2xl" />
                  </div>
                  <p className="text-[#475569] font-syne font-extrabold text-sm uppercase tracking-wider">
                    No staff members
                  </p>
                </td>
              </tr>
            ) : (
              allStaff.map((staff) => (
                <tr
                  key={staff.id}
                  className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                >
                  {/* Name */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25">
                        <span className="text-[11px] font-extrabold text-white">
                          {staff.firstName?.[0] || ""}
                          {staff.lastName?.[0] || "" || "S"}
                        </span>
                      </div>
                      <div className="font-syne text-sm font-extrabold text-[#0f172a]">
                        {staff.firstName} {staff.lastName}
                      </div>
                    </div>
                  </td>
                  {/* Email */}
                  <td className="px-5 py-3.5 text-[12px] text-[#475569] font-bold">
                    {staff.email}
                  </td>
                  {/* Role */}
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-b-[2px] ${getRoleBadge(staff.role)}`}
                    >
                      {staff.role?.replace(/_/g, " ")}
                    </span>
                  </td>
                  {/* Staff Type */}
                  <td className="px-5 py-3.5">
                    {getStaffTypeBadge(staff.staffType || "conversion")}
                  </td>
                  {/* Team Lead */}
                  <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">
                    {staff.role === "staff" && staff.teamLeadId ? (
                      <span className="text-[#1e3a5f] font-extrabold">
                        <i className="fas fa-user-tie mr-1.5" />
                        {getTeamLeadName(staff.teamLeadId) || "—"}
                      </span>
                    ) : (
                      <span className="text-[#94a3b8]">—</span>
                    )}
                  </td>
                  {/* Phone */}
                  <td className="px-5 py-3.5 text-[12px] text-[#475569] font-bold">
                    <a
                      href={`tel:${staff.phone}`}
                      className="text-[#2563eb] font-extrabold hover:underline"
                    >
                      {staff.phone || "—"}
                    </a>
                  </td>
                  {/* FreJun Number */}
                  <td className="px-5 py-3.5 text-[11px] font-mono font-bold">
                    {staff.frejunNumber ? (
                      <span className="text-[#0d9488] bg-[#f0fdfa] border border-[#99f6e4] px-2 py-1 rounded-lg">
                        <i className="fas fa-phone-alt mr-1 text-[9px]" />{" "}
                        {staff.frejunNumber}
                      </span>
                    ) : (
                      <span className="text-[#94a3b8]">—</span>
                    )}
                  </td>
                  {/* FreJun Email */}
                  <td className="px-5 py-3.5 text-[11px] font-mono font-bold">
                    {staff.frejunEmail ? (
                      <span className="text-[#0d9488] bg-[#f0fdfa] border border-[#99f6e4] px-2 py-1 rounded-lg">
                        <i className="fas fa-envelope mr-1 text-[9px]" />{" "}
                        {staff.frejunEmail}
                      </span>
                    ) : (
                      <span className="text-[#94a3b8]">—</span>
                    )}
                  </td>
                  {/* Assigned Emails */}
                  <td className="px-5 py-3.5 text-[11px] font-mono font-bold">
                    {staff.smtpEmails && staff.smtpEmails.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {(typeof staff.smtpEmails === "string"
                          ? JSON.parse(staff.smtpEmails)
                          : staff.smtpEmails
                        ).map((e, i) => (
                          <span
                            key={i}
                            className="text-[#2563eb] bg-[#eff6ff] border border-[#bfdbfe] px-2 py-0.5 rounded-lg"
                          >
                            <i className="fas fa-mail-bulk mr-1 text-[9px]" />{" "}
                            {e.name || e.email}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[#94a3b8]">—</span>
                    )}
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-b-[2px] ${
                        staff.isActive
                          ? "bg-[#059669] text-white border-[#047857]"
                          : "bg-[#dc2626] text-white border-[#b91c1c]"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${staff.isActive ? "bg-white" : "bg-white"}`}
                      />
                      {staff.isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className="px-5 py-3.5">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(staff)}
                        className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                      >
                        <i className="fas fa-pen mr-1" /> EDIT
                      </button>
                      <button
                        onClick={() => handleToggleActive(staff)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[2px] ${
                          staff.isActive
                            ? "bg-[#d97706] text-white border-[#b45309] hover:bg-[#b45309]"
                            : "bg-[#059669] text-white border-[#047857] hover:bg-[#047857]"
                        }`}
                      >
                        <i className={`fas fa-power-off mr-1`} />
                        {staff.isActive ? "DEACTIVATE" : "ACTIVATE"}
                      </button>
                      {staff.id !== adminUser?.id && (
                        <button
                          onClick={() => handleDelete(staff)}
                          className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all border-b-[2px] border-[#b91c1c]"
                        >
                          <i className="fas fa-trash-alt mr-1" /> DELETE
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div
          className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={resetForm}
        >
          <div
            className="bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b-2 border-[#e2e8f0] px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-syne text-lg font-extrabold text-[#0b1e33] uppercase tracking-tight">
                {editStaff ? "EDIT STAFF" : "ADD STAFF"}
              </h3>
              <button
                onClick={resetForm}
                className="w-8 h-8 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] flex items-center justify-center hover:text-[#dc2626] hover:border-[#dc2626] hover:bg-[#fef2f2] transition-all"
              >
                <i className="fas fa-xmark" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                    FIRST NAME
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) =>
                      setForm({ ...form, firstName: e.target.value })
                    }
                    className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                    LAST NAME
                  </label>
                  <input
                    value={form.lastName}
                    onChange={(e) =>
                      setForm({ ...form, lastName: e.target.value })
                    }
                    className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  LOGIN EMAIL <span className="text-[#dc2626]">*</span>
                </label>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  PHONE
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                />
              </div>

              {/* FreJun Number */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  FREJUN VIRTUAL NUMBER
                </label>
                <input
                  value={form.frejunNumber}
                  onChange={(e) =>
                    setForm({ ...form, frejunNumber: e.target.value })
                  }
                  placeholder="+919876543210"
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                />
              </div>

              {/* FreJun Email */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  FREJUN LOGIN EMAIL
                </label>
                <input
                  value={form.frejunEmail}
                  onChange={(e) =>
                    setForm({ ...form, frejunEmail: e.target.value })
                  }
                  placeholder="rohan@groxion.com"
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                />
                <p className="text-[9px] text-[#64748b] font-mono font-bold mt-1.5 flex items-center gap-1">
                  <i className="fas fa-info-circle text-[#0d9488]" /> The email
                  used to log into FreJun for this agent.
                </p>
              </div>

              {/* Assigned Email Accounts */}
              <div className="p-4 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider">
                    <i className="fas fa-mail-bulk mr-1.5" /> EMAIL ACCOUNTS
                  </label>
                  <button
                    onClick={addEmailAccount}
                    type="button"
                    className="px-3 py-1 rounded-lg bg-[#059669] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all"
                  >
                    <i className="fas fa-plus mr-1" /> ADD EMAIL
                  </button>
                </div>
                <p className="text-[9px] text-[#64748b] font-mono font-bold">
                  Add the email accounts this staff member can use to send
                  emails to leads. These will appear in the "Select Sender"
                  dropdown.
                </p>

                <div className="space-y-3">
                  {form.smtpEmails.map((acc, index) => (
                    <div
                      key={index}
                      className="flex gap-2 items-start bg-white p-3 rounded-lg border-2 border-[#cbd5e1]"
                    >
                      <div className="flex-1 space-y-2">
                        <input
                          value={acc.name || ""}
                          onChange={(e) =>
                            handleEmailChange(index, "name", e.target.value)
                          }
                          placeholder="Display Name (e.g. Sales Team)"
                          className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                        />
                        <input
                          value={acc.email || ""}
                          onChange={(e) =>
                            handleEmailChange(index, "email", e.target.value)
                          }
                          placeholder="Real Email (e.g. sales@groxion.com)"
                          className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                        />
                        <input
                          type="password"
                          value={acc.password || ""}
                          onChange={(e) =>
                            handleEmailChange(index, "password", e.target.value)
                          }
                          placeholder="App Password"
                          className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                        />
                      </div>
                      <button
                        onClick={() => removeEmailAccount(index)}
                        type="button"
                        className="w-8 h-8 rounded-lg bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] hover:bg-[#fee2e2] flex items-center justify-center transition-all mt-1"
                      >
                        <i className="fas fa-trash-alt text-[10px]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  CRM LOGIN PASSWORD{" "}
                  {editStaff ? (
                    "(LEAVE BLANK TO KEEP)"
                  ) : (
                    <span className="text-[#dc2626]">*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all placeholder:text-[#94a3b8]"
                />
              </div>

              {/* Role */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  ROLE
                </label>
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm({ ...form, role: e.target.value, teamLeadId: "" })
                  }
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all cursor-pointer"
                >
                  <option value="staff">STAFF</option>
                  <option value="team_lead">TEAM LEAD</option>
                  <option value="manager">MANAGER</option>
                  <option value="admin">ADMIN</option>
                </select>
              </div>

              {/* Staff Type */}
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  STAFF TYPE <span className="text-[#dc2626]">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {STAFF_TYPES.map((t) => {
                    const active = form.staffType === t;
                    const isRetention = t === "retention";
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() =>
                          setForm({ ...form, staffType: t, teamLeadId: "" })
                        }
                        className={`px-4 py-3 rounded-xl text-[11px] font-mono font-extrabold uppercase tracking-wider transition-all border-2 border-b-[3px] flex items-center justify-center gap-2 ${
                          active
                            ? isRetention
                              ? "bg-[#d97706] text-white border-[#b45309]"
                              : "bg-[#059669] text-white border-[#047857]"
                            : "bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                        }`}
                      >
                        <i
                          className={`fas ${isRetention ? "fa-lock" : "fa-rotate"} text-[10px]`}
                        />
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Team Lead (only for staff role) */}
              {form.role === "staff" && (
                <div>
                  <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                    ASSIGNED TEAM LEAD{" "}
                    <span className="text-[#94a3b8]">(OPTIONAL)</span>
                  </label>
                  <select
                    value={form.teamLeadId || ""}
                    onChange={(e) =>
                      setForm({ ...form, teamLeadId: e.target.value })
                    }
                    className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all cursor-pointer"
                  >
                    <option value="">— NO TEAM LEAD —</option>
                    {eligibleTeamLeads.map((tl) => (
                      <option key={tl.id} value={tl.id}>
                        {tl.firstName} {tl.lastName} ({tl.staffType})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Main Tab Permissions */}
              <div className="p-4 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl">
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-3">
                  <i className="fas fa-tabs mr-1.5" /> TAB ACCESS
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  {Object.entries(MAIN_PERM_LABELS).map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2.5 text-[11px] text-[#0f172a] font-bold cursor-pointer bg-white border-2 border-[#e2e8f0] rounded-lg px-3 py-2 hover:border-[#1e3a5f] transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={form.permissions[key] || false}
                        onChange={() => togglePermission(key)}
                        className="w-4 h-4 accent-[#1e3a5f] cursor-pointer"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* User Detail Permissions */}
              <div className="p-4 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl">
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-3">
                  <i className="fas fa-user-gear mr-1.5" /> USER DETAIL
                  PERMISSIONS
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  {Object.entries(USER_DETAIL_PERM_LABELS).map(
                    ([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center gap-2.5 text-[11px] text-[#0f172a] font-bold cursor-pointer bg-white border-2 border-[#e2e8f0] rounded-lg px-3 py-2 hover:border-[#1e3a5f] transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions[key] || false}
                          onChange={() => togglePermission(key)}
                          className="w-4 h-4 accent-[#1e3a5f] cursor-pointer"
                        />
                        {label}
                      </label>
                    ),
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={resetForm}
                  className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-3 rounded-xl bg-[#1e3a5f] text-white font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 transition-all border-b-[3px] border-[#0b1e33]"
                >
                  {isSubmitting
                    ? "SAVING…"
                    : editStaff
                      ? "UPDATE STAFF"
                      : "CREATE STAFF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
